import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { TerminalSquare, LogOut, RefreshCcw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { Input } from '../ui/input';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';
import SoundSettings from './SoundSettings';
import { TopNav, MobileMenuButton } from './TopNav';
import { useNavigate } from 'react-router-dom';

export default function Header({ timeframe, onTimeframeChange, onRefresh, refreshing, sound, onMenu }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const initials = (user?.name || user?.email || '?').slice(0, 2).toUpperCase();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            await apiClient.delete('/auth/me');
            toast.success('Hesabınız ve tüm verileriniz kalıcı olarak silindi');
            setTimeout(() => logout(), 800);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Hesap silinemedi');
            setDeleting(false);
        }
    };

    return (
        <header className="sticky top-0 z-30 bg-slate-900/80 backdrop-blur-md border-b border-slate-700">
            <div className="max-w-[1920px] mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-4">
                <MobileMenuButton onClick={onMenu} />
                <button onClick={() => navigate('/')} className="flex items-center gap-2 min-w-0 shrink-0" data-testid="header-brand" title="Ana Sayfa">
                    <div className="relative shrink-0">
                        <TerminalSquare className="h-5 w-5 text-emerald-400" />
                        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                    </div>
                    <span className="text-sm font-semibold tracking-tight text-slate-100">CDS</span>
                    <span className="hidden xl:inline text-[10px] uppercase tracking-[0.2em] text-slate-400 ml-1 border-l border-slate-700 pl-3">
                        Decision Support
                    </span>
                </button>

                <TopNav />

                <div className="flex-1" />

                {/* Fixed short-horizon mode — label replaces old 15m/4h toggle */}
                <div
                    data-testid="timeframe-label"
                    className="hidden lg:inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-[10px] uppercase tracking-wider text-emerald-200 shrink-0"
                    title="Sistem 15 dk - 48 saat ufku ile kısa vadeli fırsatları tarar"
                >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                    Kısa Vade · 1-48 sa
                </div>

                {sound && <SoundSettings sound={sound} />}

                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRefresh}
                    disabled={refreshing}
                    data-testid="header-refresh-button"
                    className="h-8 px-2 sm:px-3 text-xs text-slate-300 hover:text-slate-100 hover:bg-slate-700 shrink-0"
                >
                    <RefreshCcw className={`h-3.5 w-3.5 sm:mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Yenile</span>
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="profile-dropdown-trigger"
                            className="h-8 w-8 flex items-center justify-center rounded-sm border border-slate-700 bg-slate-800 hover:bg-slate-700 text-[11px] font-medium text-slate-200"
                        >
                            {initials}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-slate-100 w-56">
                        <DropdownMenuLabel className="text-xs text-slate-400 font-normal">
                            {user?.email}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-slate-600" />
                        <DropdownMenuItem
                            onClick={logout}
                            data-testid="logout-menu-item"
                            className="text-rose-400 focus:text-rose-300 focus:bg-rose-500/10 cursor-pointer"
                        >
                            <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-slate-600" />
                        <DropdownMenuItem
                            onSelect={(e) => {
                                e.preventDefault();
                                setConfirmText('');
                                setDeleteOpen(true);
                            }}
                            data-testid="delete-account-menu-item"
                            className="text-rose-500 focus:text-rose-400 focus:bg-rose-500/10 cursor-pointer"
                        >
                            <Trash2 className="h-4 w-4 mr-2" /> Hesabı Kalıcı Olarak Sil
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent data-testid="delete-account-dialog" className="bg-slate-800 border-slate-700 text-slate-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-rose-400">Hesabı Kalıcı Olarak Sil</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-300 space-y-2">
                            <span className="block">
                                Bu işlem geri alınamaz. Hesabınız, tüm pozisyonlarınız, favorileriniz ve raporlarınız MongoDB'den kalıcı olarak silinecektir.
                            </span>
                            <span className="block">
                                Onaylamak için aşağıya <span className="font-mono font-semibold text-rose-300">SIL</span> yazın:
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <Input
                        data-testid="delete-account-confirm-input"
                        autoFocus
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="SIL"
                        className="bg-slate-700 border-slate-700 text-slate-100 font-mono uppercase tracking-widest"
                    />
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="delete-account-cancel-btn"
                            disabled={deleting}
                            className="bg-slate-700 border-slate-700 text-slate-200 hover:bg-slate-600"
                        >
                            Vazgeç
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-account-confirm-btn"
                            disabled={confirmText.trim().toUpperCase() !== 'SIL' || deleting}
                            onClick={handleDeleteAccount}
                            className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-40"
                        >
                            {deleting ? 'Siliniyor…' : 'Hesabımı Sil'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </header>
    );
}
